import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, X, Edit } from 'lucide-react';
import api from '../../api';
import BrandNavbar from '../dashboard/BrandNavbar';
import CampaignReviewModal from './CampaignReviewModal';

const CampaignDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [campaign, setCampaign] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'
  const [states, setStates] = useState([]);
  const [interests, setInterests] = useState([]);
  const [targetInterests, setTargetInterests] = useState([]);
  const [requiredPlatforms, setRequiredPlatforms] = useState([]);
  const [interestSearch, setInterestSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [assignedcreator, setAssignedcreator] = useState(null);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [existingReview, setExistingReview] = useState(null);

  // Check if URL has edit parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('edit') === 'true') {
      setEditMode(true);
    }
  }, [location.search]);

  // Fetch states for targeting dropdown
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await api.get('/states');
        setStates(response.data);
      } catch (err) {
        // Error handled silently
      }
    };

    const fetchInterests = async () => {
      try {
        const response = await api.get('/interests/grouped');

        // Convert grouped interests to flat array
        const allInterests = [];
        Object.entries(response.data).forEach(([category, items]) => {
          items.forEach(item => {
            allInterests.push({ category, name: item, id: `${category}-${item}` });
          });
        });

        setInterests(allInterests);
      } catch (err) {
        setInterests([]);
      }
    };

    fetchStates();
    fetchInterests();
  }, []);

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const res = await api.get(`/campaigns/${id}`);
        setCampaign(res.data);
        setForm({
          title: res.data.title || '',
          budget: res.data.budget || '',
          status: res.data.status || '',
          deadline: res.data.deadline || '',
          biddingEndDate: res.data.biddingEndDate || '',
          brandLogoUrl: res.data.brandLogoUrl || '',
          description: res.data.description || '',
          numBids: res.data.numBids || 0,
          performanceMetrics: res.data.performanceMetrics || '',
          targetState: res.data.targetState || '',
          targetCity: res.data.targetCity || '',
        });

        // Initialize targetInterests from campaign data
        if (res.data.targetInterests) {
          const interestsArray = res.data.targetInterests.split(',').map(i => i.trim()).filter(i => i);
          setTargetInterests(interestsArray);
        } else {
          setTargetInterests([]);
        }

        // Initialize requiredPlatforms from campaign data
        if (res.data.requiredPlatforms) {
          const platformsArray = res.data.requiredPlatforms.split(',').map(p => p.trim()).filter(p => p);
          setRequiredPlatforms(platformsArray);
        } else {
          setRequiredPlatforms([]);
        }

        // Fetch completion status
        try {
          const completionRes = await api.get(`/campaigns/${id}/completion-status`);
          setCompletionStatus(completionRes.data);
        } catch (err) {
          // Error handled silently
        }

        // If campaign has assigned creator, fetch their info and check for existing review
        if (res.data.creator) {
          setAssignedcreator({
            id: res.data.creator.id,
            name: res.data.creator.name
          });

          // Check if review exists
          try {
            const reviewRes = await api.get(`/campaigns/${id}/reviews/creator/${res.data.creator.id}`);
            if (reviewRes.data && reviewRes.data.id) {
              setExistingReview(reviewRes.data);
            }
          } catch (err) {
            // Error handled silently
          }
        }
      } catch (err) {
        setMessage('Failed to load campaign details.');
        setMessageType('error');
      }
    };

    fetchCampaignData();
  }, [id]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Add interest to selected list
  const addInterest = (interest) => {
    if (!targetInterests.includes(interest.name)) {
      setTargetInterests(prev => [...prev, interest.name]);
    }
    setInterestSearch('');
  };

  // Remove interest from selected list
  const removeInterest = (interestToRemove) => {
    setTargetInterests(prev => prev.filter(interest => interest !== interestToRemove));
  };

  // Toggle platform selection
  const togglePlatform = (platform) => {
    if (requiredPlatforms.includes(platform)) {
      setRequiredPlatforms(prev => prev.filter(p => p !== platform));
    } else {
      setRequiredPlatforms(prev => [...prev, platform]);
    }
  };

  // Filter interests based on search and category
  const filteredInterests = interests.filter(interest => {
    const matchesSearch = interest.name.toLowerCase().includes(interestSearch.toLowerCase());
    const matchesCategory = !selectedCategory || interest.category === selectedCategory;
    const notSelected = !targetInterests.includes(interest.name);
    return matchesSearch && matchesCategory && notSelected;
  });

  // Get unique categories from interests
  const categories = [...new Set(interests.map(i => i.category))].sort();

  const handleSave = async () => {
    try {
      setMessage('Updating campaign...');
      setMessageType('info');

      // Include targetInterests and requiredPlatforms in the update
      const updateData = {
        ...form,
        targetInterests: targetInterests.join(','),
        requiredPlatforms: requiredPlatforms.join(',')
      };

      await api.put(`/campaigns/${id}`, updateData);
      setCampaign({ ...campaign, ...updateData });
      setEditMode(false);
      setMessage('Campaign updated successfully!');
      setMessageType('success');
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
    } catch (error) {
      setMessage(`Update failed: ${error.response?.data || error.message}`);
      setMessageType('error');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
    }
  };

  if (!campaign) return (
    <>
      <Helmet>
        <title>Loading Campaign - Brand Dashboard | CollabOrb</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <BrandNavbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="inline-block animate-pulse">
              <div className="text-xl font-semibold text-gray-700">Loading campaign details...</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const campaignTitle = campaign.title || 'Campaign';
  const pageTitle = `${campaignTitle} - Campaign Details | CollabOrb`;
  const pageDescription = `Manage your ${campaignTitle} campaign on CollabOrb. Budget: ₹${Number(campaign.budget).toLocaleString('en-IN')}, Status: ${campaign.status}. ${campaign.description?.substring(0, 100)}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${campaignTitle}, brand campaign, creator marketing, campaign management, budget ${campaign.budget}, CollabOrb`} />
        <meta name="robots" content="noindex, nofollow" />
        
        {/* Schema.org structured data for campaigns */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": campaignTitle,
            "description": campaign.description,
            "startDate": new Date().toISOString(),
            "endDate": campaign.deadline,
            "location": {
              "@type": "VirtualLocation",
              "url": "https://collaborb.com"
            },
            "organizer": {
              "@type": "Organization",
              "name": "CollabOrb",
              "url": "https://collaborb.com"
            },
            "offers": {
              "@type": "Offer",
              "price": campaign.budget,
              "priceCurrency": "INR"
            }
          })}
        </script>
      </Helmet>

      <BrandNavbar />

      {/* Full-width container with modern layout */}
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className={`mx-auto px-4 sm:px-6 lg:px-8 py-8 ${editMode ? 'max-w-full' : 'max-w-7xl'}`}>

          {/* Header Section */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                {campaign.brandLogoUrl && (
                  <img
                    src={campaign.brandLogoUrl}
                    alt="Brand Logo"
                    className="h-16 w-16 object-contain rounded-lg border border-gray-200"
                  />
                )}
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{campaign.title}</h1>
                  <p className="text-sm text-gray-500 mt-1">Campaign Management</p>
                </div>
              </div>

              {/* Edit/Cancel buttons */}
              {!editMode ? (
                <button
                  className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 shadow-sm"
                  onClick={() => setEditMode(true)}
                >
                  <Edit size={18} />
                  <span>Edit Campaign</span>
                </button>
              ) : (
                <button
                  className="text-gray-600 px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setEditMode(false);
                    setMessage('');
                    setMessageType('');
                  }}
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Message Display */}
            {message && (
              <div className={`px-6 py-4 border-b border-gray-200 ${
                messageType === 'success' ? 'bg-green-50' : 
                messageType === 'error' ? 'bg-red-50' : 
                'bg-blue-50'
              }`}>
                <p className={`text-sm font-medium ${
                  messageType === 'success' ? 'text-green-800' : 
                  messageType === 'error' ? 'text-red-800' : 
                  'text-blue-800'
                }`}>
                  {message}
                </p>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200">
            {editMode ? (
              <div className="px-6 py-8 md:px-10 lg:px-16 xl:px-24 2xl:px-32">
                {/* Header */}
                <div className="mb-8 pb-6 border-b-2 border-gray-200">
                  <h2 className="text-3xl font-bold text-gray-900 flex items-center">
                    <span className="text-4xl mr-3">✏️</span>
                    Edit Campaign Details
                  </h2>
                  <p className="text-sm text-gray-600 mt-2">Update your campaign information and targeting preferences</p>
                </div>

                {/* Basic Information Section */}
                <div className="mb-10">
                  <div className="flex items-center mb-6">
                    <span className="text-2xl mr-3">📋</span>
                    <h3 className="text-xl font-bold text-gray-800">Basic Information</h3>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">

                  {/* Campaign Name */}
                  <div className="lg:col-span-2">
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">🏷️</span>
                      Campaign Name
                    </label>
                    <input
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all hover:border-gray-400 shadow-sm"
                      placeholder="Enter campaign title"
                      required
                    />
                  </div>

                  {/* Budget */}
                  <div>
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">💰</span>
                      Budget (₹)
                    </label>
                    <input
                      name="budget"
                      type="number"
                      value={form.budget}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all hover:border-gray-400 shadow-sm"
                      placeholder="0.00"
                      min="0"
                      step="100"
                      required
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📊</span>
                      Status
                    </label>
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all hover:border-gray-400 bg-white shadow-sm cursor-pointer"
                    >
                      <option value="Active">✅ Active</option>
                      <option value="Completed">🎉 Completed</option>
                      <option value="Paused">⏸️ Paused</option>
                      <option value="Draft">📝 Draft</option>
                    </select>
                  </div>

                  {/* Bidding End Date */}
                  <div>
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">⏰</span>
                      Bidding End Date
                    </label>
                    <input
                      name="biddingEndDate"
                      type="date"
                      value={form.biddingEndDate}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all hover:border-gray-400 shadow-sm"
                    />
                  </div>

                  {/* Deadline */}
                  <div>
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📅</span>
                      Project Deadline
                    </label>
                    <input
                      name="deadline"
                      type="date"
                      value={form.deadline}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all hover:border-gray-400 shadow-sm"
                    />
                  </div>

                  {/* Brand Logo URL */}
                  <div className="lg:col-span-2">
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">🖼️</span>
                      Brand Logo URL
                    </label>
                    <input
                      name="brandLogoUrl"
                      type="url"
                      value={form.brandLogoUrl}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all hover:border-gray-400 shadow-sm"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>

                  {/* Description */}
                  <div className="lg:col-span-2">
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📝</span>
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all hover:border-gray-400 resize-none shadow-sm"
                      placeholder="Describe your campaign objectives and requirements"
                      rows="5"
                    />
                  </div>

                  {/* Number of Bids */}
                  <div>
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">🙋</span>
                      Number of Bids Received
                    </label>
                    <input
                      name="numBids"
                      type="number"
                      value={form.numBids}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg bg-gray-100 cursor-not-allowed shadow-sm"
                      placeholder="0"
                      min="0"
                      readOnly
                      title="This field is automatically updated"
                    />
                  </div>

                  {/* Performance Metrics */}
                  <div>
                    <label className="text-base font-bold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📊</span>
                      Performance Metrics
                    </label>
                    <textarea
                      name="performanceMetrics"
                      value={form.performanceMetrics}
                      onChange={handleChange}
                      className="w-full border-2 border-gray-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all hover:border-gray-400 resize-none shadow-sm"
                      placeholder="Track campaign performance, engagement metrics, etc."
                      rows="4"
                    />
                  </div>
                </div>
              </div>

                {/* Targeting Criteria Section */}
                <div className="mt-12 pb-10 border-t-2 border-gray-200 pt-10">
                  <div className="mb-8">
                    <div className="flex items-center mb-3">
                      <span className="text-3xl mr-3">🎯</span>
                      <h3 className="text-2xl font-bold text-gray-900">Targeting Criteria</h3>
                    </div>
                    <p className="text-base text-gray-600 ml-12">
                      Define who can see and bid on this campaign based on location and interests
                    </p>
                  </div>

                  {/* Location Targeting */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border-2 border-blue-200 mb-8">
                    <h4 className="text-lg font-bold text-blue-900 mb-6 flex items-center">
                      <span className="text-2xl mr-2">📍</span>
                      Location Targeting
                    </h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {/* Target State */}
                    <div>
                      <label className="block text-base font-bold text-blue-900 mb-3">
                        Target State
                      </label>
                      <select
                        name="targetState"
                        value={form.targetState}
                        onChange={(e) => {
                          handleChange(e);
                          // Reset city when state changes
                          if (e.target.value !== form.targetState) {
                            setForm(prev => ({ ...prev, targetCity: '' }));
                          }
                        }}
                        className="w-full border-2 border-blue-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all bg-white shadow-sm hover:border-blue-400"
                      >
                        <option value="">🌍 All States (No Filter)</option>
                        {states.map((state) => (
                          <option key={state.id} value={state.stateName}>
                            {state.stateName}
                          </option>
                        ))}
                      </select>
                      <p className="text-sm text-blue-800 mt-2 ml-1">
                        Select a specific state to target creators from that region
                      </p>
                    </div>

                    {/* Target City */}
                    <div>
                      <label className="block text-base font-bold text-blue-900 mb-3">
                        Target City
                      </label>
                      <input
                        type="text"
                        name="targetCity"
                        placeholder="Enter city name (optional)"
                        value={form.targetCity}
                        onChange={handleChange}
                        className="w-full border-2 border-blue-300 rounded-xl px-5 py-4 text-lg focus:ring-4 focus:ring-blue-200 focus:border-blue-500 transition-all disabled:bg-blue-100 disabled:cursor-not-allowed shadow-sm hover:border-blue-400"
                        disabled={!form.targetState}
                      />
                      <p className="text-sm text-blue-800 mt-2 ml-1">
                        {form.targetState
                          ? "Narrow down to a specific city within the selected state"
                          : "⚠️ Select a state first to enable city targeting"}
                      </p>
                    </div>
                  </div>
                </div>

                  {/* Interest Targeting Section */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border-2 border-purple-200">
                    <h4 className="text-lg font-bold text-purple-900 mb-4 flex items-center">
                      <span className="text-2xl mr-2">💡</span>
                      Interest Targeting
                    </h4>
                    <label className="block text-base font-bold text-purple-900 mb-3">
                      Target Interests
                    </label>
                    <p className="text-sm text-purple-800 mb-4">
                      Select interests to target specific creators. Campaign will be visible to creators with ANY of the selected interests.
                    </p>

                      {/* Category Filters */}
                      {interests.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          <button
                            type="button"
                            onClick={() => setSelectedCategory('')}
                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                              selectedCategory === '' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            All Categories
                          </button>
                          {categories.map(category => (
                            <button
                              type="button"
                              key={category}
                              onClick={() => setSelectedCategory(category)}
                              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                selectedCategory === category 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {category}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Search Input */}
                      {interests.length > 0 && (
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={interestSearch}
                            onChange={(e) => setInterestSearch(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="Search interests..."
                          />
                        </div>
                      )}

                      {/* Available Interests to Add */}
                      {(interestSearch || selectedCategory) && filteredInterests.length > 0 && (
                        <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto mb-3">
                          {Object.entries(
                            filteredInterests.reduce((acc, interest) => {
                              if (!acc[interest.category]) acc[interest.category] = [];
                              acc[interest.category].push(interest);
                              return acc;
                            }, {})
                          ).map(([category, categoryInterests]) => (
                            <div key={category} className="mb-3 last:mb-0">
                              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                <span className="px-2 py-0.5 bg-gray-100 rounded text-xs mr-2">{category}</span>
                                <span className="text-gray-500 text-xs">({categoryInterests.length})</span>
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {categoryInterests.map(interest => (
                                  <button
                                    type="button"
                                    key={interest.id}
                                    onClick={() => addInterest(interest)}
                                    className="px-2.5 py-1 text-xs bg-white border border-gray-200 hover:bg-blue-50 hover:border-blue-300 text-gray-700 rounded-lg transition-colors"
                                  >
                                    {interest.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* No Results Message */}
                      {(interestSearch || selectedCategory) && filteredInterests.length === 0 && interests.length > 0 && (
                        <div className="border border-gray-200 rounded-lg p-3 text-center text-gray-500 text-xs mb-3">
                          No interests found. Try a different search or category.
                        </div>
                      )}

                      {/* Loading State */}
                      {interests.length === 0 && (
                        <div className="text-center py-3 text-gray-500 text-xs">
                          Loading interests...
                        </div>
                      )}

                      {/* Selected Interests */}
                      {targetInterests.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-2">
                            Selected Interests ({targetInterests.length}):
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {targetInterests.map((interest) => {
                              const interestObj = interests.find(i => i.name === interest);
                              const categoryTag = interestObj ? interestObj.category : '';

                              return (
                                <span
                                  key={interest}
                                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                                >
                                  {categoryTag && (
                                    <span className="text-xs font-semibold mr-1 px-1.5 py-0.5 bg-blue-200 rounded">
                                      {categoryTag}
                                    </span>
                                  )}
                                  {interest}
                                  <button
                                    type="button"
                                    onClick={() => removeInterest(interest)}
                                    className="ml-1.5 hover:text-blue-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {targetInterests.length === 0 && (
                        <p className="text-xs text-gray-500 italic">
                          No interests selected. Campaign will be visible to all creators (subject to location filters).
                        </p>
                      )}
                    </div>

                  {/* Social Media Platform Requirements Section */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border-2 border-green-200 mt-6">
                    <h4 className="text-lg font-bold text-green-900 mb-4 flex items-center">
                      <span className="text-2xl mr-2">📱</span>
                      Social Media Platform Requirements
                    </h4>
                    <label className="block text-base font-bold text-green-900 mb-3">
                      Required Platforms
                    </label>
                    <p className="text-sm text-green-800 mb-4">
                      Select platforms that Creators must have connected. Only Creators with at least one of these platforms will be able to bid.
                    </p>

                    <div className="flex flex-wrap gap-4">
                      {/* YouTube */}
                      <button
                        type="button"
                        onClick={() => togglePlatform('youtube')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                          requiredPlatforms.includes('youtube')
                            ? 'border-red-500 bg-red-50 shadow-lg scale-105'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow'
                        }`}
                        title="YouTube"
                      >
                        <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('youtube') ? '#FF0000' : '#9CA3AF'}>
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                        <span className={`text-xs mt-1 font-semibold ${requiredPlatforms.includes('youtube') ? 'text-red-600' : 'text-gray-600'}`}>
                          YouTube
                        </span>
                      </button>

                      {/* Instagram */}
                      <button
                        type="button"
                        onClick={() => togglePlatform('instagram')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                          requiredPlatforms.includes('instagram')
                            ? 'border-pink-500 bg-pink-50 shadow-lg scale-105'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow'
                        }`}
                        title="Instagram"
                      >
                        <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('instagram') ? '#E4405F' : '#9CA3AF'}>
                          <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                        </svg>
                        <span className={`text-xs mt-1 font-semibold ${requiredPlatforms.includes('instagram') ? 'text-pink-600' : 'text-gray-600'}`}>
                          Instagram
                        </span>
                      </button>

                      {/* Facebook */}
                      <button
                        type="button"
                        onClick={() => togglePlatform('facebook')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                          requiredPlatforms.includes('facebook')
                            ? 'border-blue-600 bg-blue-50 shadow-lg scale-105'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow'
                        }`}
                        title="Facebook"
                      >
                        <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('facebook') ? '#1877F2' : '#9CA3AF'}>
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        <span className={`text-xs mt-1 font-semibold ${requiredPlatforms.includes('facebook') ? 'text-blue-600' : 'text-gray-600'}`}>
                          Facebook
                        </span>
                      </button>

                      {/* Twitter/X */}
                      <button
                        type="button"
                        onClick={() => togglePlatform('twitter')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                          requiredPlatforms.includes('twitter')
                            ? 'border-black bg-gray-100 shadow-lg scale-105'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow'
                        }`}
                        title="Twitter/X"
                      >
                        <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('twitter') ? '#000000' : '#9CA3AF'}>
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        <span className={`text-xs mt-1 font-semibold ${requiredPlatforms.includes('twitter') ? 'text-black' : 'text-gray-600'}`}>
                          Twitter/X
                        </span>
                      </button>

                      {/* LinkedIn */}
                      <button
                        type="button"
                        onClick={() => togglePlatform('linkedin')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                          requiredPlatforms.includes('linkedin')
                            ? 'border-blue-700 bg-blue-50 shadow-lg scale-105'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow'
                        }`}
                        title="LinkedIn"
                      >
                        <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('linkedin') ? '#0A66C2' : '#9CA3AF'}>
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        <span className={`text-xs mt-1 font-semibold ${requiredPlatforms.includes('linkedin') ? 'text-blue-700' : 'text-gray-600'}`}>
                          LinkedIn
                        </span>
                      </button>

                      {/* TikTok */}
                      <button
                        type="button"
                        onClick={() => togglePlatform('tiktok')}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                          requiredPlatforms.includes('tiktok')
                            ? 'border-black bg-gray-100 shadow-lg scale-105'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow'
                        }`}
                        title="TikTok"
                      >
                        <svg className="w-10 h-10" viewBox="0 0 24 24" fill={requiredPlatforms.includes('tiktok') ? '#000000' : '#9CA3AF'}>
                          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                        </svg>
                        <span className={`text-xs mt-1 font-semibold ${requiredPlatforms.includes('tiktok') ? 'text-black' : 'text-gray-600'}`}>
                          TikTok
                        </span>
                      </button>
                    </div>

                    {requiredPlatforms.length === 0 && (
                      <p className="text-xs text-green-700 italic mt-4 bg-green-50 border-l-4 border-green-500 p-3 rounded">
                        ℹ️ No platform selected. All Creators can bid regardless of connected platforms.
                      </p>
                    )}

                    {requiredPlatforms.length > 0 && (
                      <div className="mt-4 p-3 bg-green-100 border-2 border-green-400 rounded-lg">
                        <p className="text-sm text-green-900 font-semibold">
                          ✅ Selected: Only Creators with <span className="font-bold">{requiredPlatforms.join(', ')}</span> will be able to bid on this campaign.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-12 pt-8 border-t-2 border-gray-200">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 -mx-6 md:-mx-10 lg:-mx-16 xl:-mx-24 2xl:-mx-32 px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-8 sticky bottom-0 shadow-2xl rounded-b-2xl border-t-4 border-blue-500">
                    <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                      <button
                        className="group bg-gradient-to-r from-blue-600 to-blue-700 text-white px-12 py-5 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-bold shadow-lg text-lg flex-shrink-0 flex items-center transform hover:scale-105 hover:shadow-2xl"
                        onClick={handleSave}
                      >
                        <span className="mr-3 text-2xl group-hover:animate-pulse">💾</span>
                        Save Changes
                      </button>
                      <button
                        className="group text-gray-700 px-12 py-5 border-3 border-gray-400 rounded-xl hover:bg-white hover:border-gray-600 transition-all duration-300 font-bold text-lg flex-shrink-0 transform hover:scale-105 shadow-md hover:shadow-lg"
                        onClick={() => {
                          setEditMode(false);
                          setMessage('');
                          setMessageType('');
                        }}
                      >
                        <span className="mr-3 text-xl">✕</span>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Read-only view */
              <div className="p-6 md:p-8 lg:p-12">
                {/* Campaign Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                  <div className="group bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Budget</p>
                      <span className="text-2xl">💰</span>
                    </div>
                    <p className="text-3xl font-bold text-blue-900 mt-2">₹{Number(campaign.budget).toLocaleString('en-IN')}</p>
                    <p className="text-xs text-blue-700 mt-2 opacity-75">Total Campaign Budget</p>
                  </div>

                  <div className="group bg-gradient-to-br from-green-50 via-green-50 to-green-100 p-6 rounded-xl border-2 border-green-200 hover:border-green-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-green-600 uppercase tracking-wide">Status</p>
                      <span className="text-2xl">
                        {campaign.status === 'Active' ? '✅' :
                         campaign.status === 'Completed' ? '🎉' :
                         campaign.status === 'Paused' ? '⏸️' : '📝'}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-green-900 mt-2">{campaign.status}</p>
                    <p className="text-xs text-green-700 mt-2 opacity-75">Current Status</p>
                  </div>

                  <div className="group bg-gradient-to-br from-purple-50 via-purple-50 to-purple-100 p-6 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-purple-600 uppercase tracking-wide">Bids</p>
                      <span className="text-2xl">🙋</span>
                    </div>
                    <p className="text-3xl font-bold text-purple-900 mt-2">{campaign.numBids || 0}</p>
                    <p className="text-xs text-purple-700 mt-2 opacity-75">Proposals Received</p>
                  </div>

                  <div className="group bg-gradient-to-br from-orange-50 via-orange-50 to-orange-100 p-6 rounded-xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-orange-600 uppercase tracking-wide">Deadline</p>
                      <span className="text-2xl">⏰</span>
                    </div>
                    <p className="text-base font-bold text-orange-900 mt-2">
                      {campaign.biddingEndDate
                        ? new Date(campaign.biddingEndDate).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
                        : 'Not set'
                      }
                    </p>
                    <p className="text-xs text-orange-700 mt-2 opacity-75">Bidding Closes</p>
                  </div>
                </div>

                {/* Campaign Details */}
                <div className="space-y-6">
                  {/* Campaign Information Card */}
                  <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b-2 border-gray-200">
                      <h3 className="text-lg font-bold text-gray-800 flex items-center">
                        <span className="text-2xl mr-3">📋</span>
                        Campaign Information
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 pb-4 border-b border-gray-200">
                        <span className="text-sm font-semibold text-gray-600 flex items-center">
                          <span className="mr-2">🏷️</span>
                          Campaign Name
                        </span>
                        <span className="text-sm text-gray-900 font-bold md:text-right md:max-w-md">{campaign.title}</span>
                      </div>

                      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 pb-4 border-b border-gray-200">
                        <span className="text-sm font-semibold text-gray-600 flex items-center">
                          <span className="mr-2">📅</span>
                          Project Deadline
                        </span>
                        <span className="text-sm text-gray-900 font-medium md:text-right">
                          {campaign.deadline
                            ? new Date(campaign.deadline).toLocaleDateString('en-IN', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })
                            : 'Not set'
                          }
                        </span>
                      </div>

                      {campaign.brandLogoUrl && (
                        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 pb-4 border-b border-gray-200">
                          <span className="text-sm font-semibold text-gray-600 flex items-center">
                            <span className="mr-2">🖼️</span>
                            Brand Logo
                          </span>
                          <img src={campaign.brandLogoUrl} alt="Brand" className="h-12 w-12 object-contain rounded-lg border-2 border-gray-200" />
                        </div>
                      )}
                    </div>
                  </div>

                {/* Assigned creator & Review Section */}
                {assignedcreator && (
                  <div className="bg-white border-2 border-purple-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b-2 border-purple-200">
                      <h3 className="text-lg font-bold text-purple-800 flex items-center">
                        <span className="text-2xl mr-3">👤</span>
                        Assigned creator
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                        <div>
                          <p className="text-lg font-semibold text-gray-900">{assignedcreator.name}</p>
                          {completionStatus?.completedBycreator && (
                            <div className="mt-2 inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Work Completed
                              {completionStatus.completedAt && (
                                <span className="ml-2 text-xs opacity-75">
                                  {new Date(completionStatus.completedAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-3">
                          {existingReview ? (
                            <div className="text-right">
                              <div className="flex items-center justify-end mb-1">
                                {[...Array(5)].map((_, i) => (
                                  <svg
                                    key={i}
                                    className={`w-5 h-5 ${
                                      i < existingReview.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                                    }`}
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                              </div>
                              <p className="text-sm text-gray-600">Reviewed on {new Date(existingReview.createdAt).toLocaleDateString()}</p>
                              {existingReview.reviewText && (
                                <p className="text-sm text-gray-700 mt-2 italic">"{existingReview.reviewText}"</p>
                              )}
                            </div>
                          ) : completionStatus?.completedBycreator ? (
                            <button
                              onClick={() => setShowReviewModal(true)}
                              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                              </svg>
                              Write a Review
                            </button>
                          ) : (
                            <div className="text-sm text-gray-500 italic">
                              Review available after creator completes work
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Targeting Criteria Section */}
                {(campaign.targetState || campaign.targetCity || campaign.targetInterests || campaign.requiredPlatforms) && (
                  <div className="bg-white border-2 border-blue-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b-2 border-blue-200">
                      <h3 className="text-lg font-bold text-blue-800 flex items-center">
                        <span className="text-2xl mr-3">🎯</span>
                        Targeting Criteria
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      {campaign.targetState && (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <span className="text-sm font-semibold text-gray-700 flex items-center">
                            <span className="mr-2">📍</span>
                            Target State:
                          </span>
                          <span className="inline-flex items-center px-4 py-2 bg-blue-50 border-2 border-blue-300 text-blue-800 rounded-full text-sm font-bold shadow-sm">
                            {campaign.targetState}
                          </span>
                        </div>
                      )}
                      {campaign.targetCity && (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <span className="text-sm font-semibold text-gray-700 flex items-center">
                            <span className="mr-2">🏙️</span>
                            Target City:
                          </span>
                          <span className="inline-flex items-center px-4 py-2 bg-blue-50 border-2 border-blue-300 text-blue-800 rounded-full text-sm font-bold shadow-sm">
                            {campaign.targetCity}
                          </span>
                        </div>
                      )}
                      {campaign.targetInterests && (
                        <div>
                          <span className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                            <span className="mr-2">💡</span>
                            Target Interests:
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {campaign.targetInterests.split(',').map((interest, idx) => {
                              const trimmedInterest = interest.trim();
                              const interestObj = interests.find(i => i.name === trimmedInterest);
                              const categoryTag = interestObj ? interestObj.category : '';

                              return (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-3 py-2 rounded-lg text-sm bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-300 text-purple-800 font-medium shadow-sm hover:shadow-md transition-all"
                                >
                                  {categoryTag && (
                                    <span className="text-xs font-bold mr-2 px-2 py-1 bg-purple-200 rounded-md text-purple-900">
                                      {categoryTag}
                                    </span>
                                  )}
                                  {trimmedInterest}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {campaign.requiredPlatforms && (
                        <div>
                          <span className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                            <span className="mr-2">📱</span>
                            Required Platforms:
                          </span>
                          <div className="flex flex-wrap gap-3">
                            {campaign.requiredPlatforms.split(',').map((platform, idx) => {
                              const trimmedPlatform = platform.trim().toLowerCase();
                              let icon, color, bgColor, borderColor;

                              switch(trimmedPlatform) {
                                case 'youtube':
                                  icon = <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
                                  color = 'text-red-700';
                                  bgColor = 'bg-red-50';
                                  borderColor = 'border-red-300';
                                  break;
                                case 'instagram':
                                  icon = <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#E4405F"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>;
                                  color = 'text-pink-700';
                                  bgColor = 'bg-pink-50';
                                  borderColor = 'border-pink-300';
                                  break;
                                case 'facebook':
                                  icon = <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
                                  color = 'text-blue-700';
                                  bgColor = 'bg-blue-50';
                                  borderColor = 'border-blue-300';
                                  break;
                                case 'twitter':
                                  icon = <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#000000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
                                  color = 'text-gray-900';
                                  bgColor = 'bg-gray-100';
                                  borderColor = 'border-gray-300';
                                  break;
                                case 'linkedin':
                                  icon = <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>;
                                  color = 'text-blue-800';
                                  bgColor = 'bg-blue-50';
                                  borderColor = 'border-blue-400';
                                  break;
                                case 'tiktok':
                                  icon = <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#000000"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>;
                                  color = 'text-gray-900';
                                  bgColor = 'bg-gray-100';
                                  borderColor = 'border-gray-300';
                                  break;
                                default:
                                  icon = <span className="text-lg">📱</span>;
                                  color = 'text-gray-700';
                                  bgColor = 'bg-gray-50';
                                  borderColor = 'border-gray-300';
                              }

                              return (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${bgColor} border-2 ${borderColor} ${color} font-medium shadow-sm hover:shadow-md transition-all`}
                                >
                                  {icon}
                                  <span className="capitalize font-semibold">{trimmedPlatform}</span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
                        <p className="text-sm text-blue-900 flex items-start">
                          <span className="mr-2 mt-0.5">ℹ️</span>
                          <span>
                            <strong>Visibility:</strong> This campaign is visible to creators{' '}
                            {campaign.targetState && `from ${campaign.targetCity ? `${campaign.targetCity}, ` : ''}${campaign.targetState}`}
                            {campaign.targetState && (campaign.targetInterests || campaign.requiredPlatforms) && ', '}
                            {campaign.targetInterests && `with matching interests`}
                            {campaign.targetInterests && campaign.requiredPlatforms && ', and '}
                            {campaign.requiredPlatforms && `with connected ${campaign.requiredPlatforms.split(',').map(p => p.trim()).join(', ')} account(s)`}.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Description Section */}
                {campaign.description && (
                  <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b-2 border-gray-200">
                      <h3 className="text-lg font-bold text-gray-800 flex items-center">
                        <span className="text-2xl mr-3">📝</span>
                        Description
                      </h3>
                    </div>
                    <div className="p-6">
                      <p className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap">{campaign.description}</p>
                    </div>
                  </div>
                )}
                
                {/* Performance Metrics Section */}
                {campaign.performanceMetrics && (
                  <div className="bg-white border-2 border-green-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b-2 border-green-200">
                      <h3 className="text-lg font-bold text-green-800 flex items-center">
                        <span className="text-2xl mr-3">📊</span>
                        Performance Metrics
                      </h3>
                    </div>
                    <div className="p-6">
                      <p className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap">{campaign.performanceMetrics}</p>
                    </div>
                  </div>
                )}
              </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Campaign Review Modal */}
      {showReviewModal && assignedcreator && campaign && (
        <CampaignReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          campaign={campaign}
          creator={assignedcreator}
          onReviewSubmitted={() => {
            // Refresh campaign data to show the review
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default CampaignDetailsPage;


